import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { unsubscribeEmailOptIn } from "../api/marketingEmailOptIn";
import { useAuth } from "../context/AuthContext";
import {
  getPreviewEmailPreferences,
  getPublicEmailPreferences,
  updatePublicEmailPreferences,
} from "../api/emailPreferences";

const parseTokens = (search) => {
  const params = new URLSearchParams(search);
  return {
    token: params.get("token") || "",
    prefToken: params.get("pref_token") || "",
    salonId: params.get("salon_id") || "",
    category: params.get("category") || "all_optional",
  };
};

const reasonOptions = [
  "Too many emails",
  "Not relevant",
  "Prefer text messages",
  "Moved away",
  "Other",
];

const preferenceCopy = {
  all_optional: {
    title: "All optional emails",
    description: "Feedback, reminders, refills, promotions, and salon updates.",
  },
  feedback: {
    title: "Feedback requests",
    description: "Review requests, surveys, and feedback follow-ups.",
  },
  appointment_reminders: {
    title: "Appointment reminders",
    description: "Upcoming appointment reminders and future appointment summaries.",
  },
  service_reminders: {
    title: "Refill and service reminders",
    description: "Service refill and maintenance reminders.",
  },
  promotions: {
    title: "Promotions and salon updates",
    description: "Offers, announcements, and other salon marketing emails.",
  },
};

const preferenceOrder = [
  "all_optional",
  "feedback",
  "appointment_reminders",
  "service_reminders",
  "promotions",
];

function normalizePreferences(rows) {
  const byCategory = new Map((rows || []).map((pref) => [pref.category, pref]));
  return preferenceOrder.map((category) => {
    const copy = preferenceCopy[category] || {};
    const pref = byCategory.get(category);
    return {
      category,
      label: copy.title || pref?.label || category.replaceAll("_", " "),
      description: copy.description || "",
      is_subscribed: pref ? Boolean(pref.is_subscribed) : true,
    };
  });
}

export default function EmailUnsubscribe() {
  const location = useLocation();
  const { prefToken: pathPrefToken } = useParams();
  const { token, prefToken, salonId, category } = useMemo(
    () => parseTokens(location.search),
    [location.search]
  );
  const resolvedPrefToken = prefToken || pathPrefToken || "";
  const { token: authToken } = useAuth();
  const isPreview =
    location.pathname === "/email/unsubscribe/preview" ||
    location.pathname === "/unsubscribe/preview";
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState("");
  const [preferences, setPreferences] = useState({
    promos: false,
    new_services: false,
    events: false,
    tips: false,
  });
  const [publicData, setPublicData] = useState(null);
  const [publicPreferences, setPublicPreferences] = useState([]);
  const [hardOptout, setHardOptout] = useState(false);
  const [loading, setLoading] = useState(Boolean(resolvedPrefToken || isPreview));
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const isPreferenceCenter = Boolean(resolvedPrefToken || isPreview);
  const allOptionalSubscribed =
    publicPreferences.find((pref) => pref.category === "all_optional")
      ?.is_subscribed !== false;

  useEffect(() => {
    if (!resolvedPrefToken && !isPreview) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    const request = isPreview
      ? getPreviewEmailPreferences(salonId, authToken, category)
      : getPublicEmailPreferences(resolvedPrefToken);
    request
      .then((data) => {
        if (cancelled) return;
        setPublicData(data);
        setPublicPreferences(normalizePreferences(data?.preferences));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || "Unable to load preferences.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [authToken, category, isPreview, resolvedPrefToken, salonId]);

  const submitLegacyMarketing = async (updateOnly) => {
    if (!token) return;
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        token,
        reason: reason || null,
        detail: detail || null,
        preferences_json: preferences,
        hard_optout: hardOptout,
        update_preferences_only: updateOnly,
      };
      await unsubscribeEmailOptIn(payload);
      setSuccess(updateOnly ? "Preferences updated." : "You are unsubscribed.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to update subscription.");
    } finally {
      setLoading(false);
    }
  };

  const submitPublicPreferences = async (updates) => {
    if (isPreview) {
      setSuccess("Preview only. Client preferences were not changed.");
      return;
    }
    if (!resolvedPrefToken) return;
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        token: resolvedPrefToken,
        reason: reason || null,
        detail: detail || null,
        ...updates,
      };
      const data = await updatePublicEmailPreferences(payload);
      setPublicPreferences(normalizePreferences(data?.preferences));
      setSuccess("Email preferences updated.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to update preferences.");
    } finally {
      setLoading(false);
    }
  };

  const savePublicPreferences = () => {
    const preferencePayload = {};
    publicPreferences.forEach((pref) => {
      preferencePayload[pref.category] = Boolean(pref.is_subscribed);
    });
    submitPublicPreferences({ preferences: preferencePayload });
  };

  const updateLocalPublicPreference = (category, checked) => {
    setPublicPreferences((prev) =>
      prev.map((pref) => {
        if (pref.category === category) {
          return { ...pref, is_subscribed: checked };
        }
        if (category !== "all_optional" && checked && pref.category === "all_optional") {
          return { ...pref, is_subscribed: true };
        }
        return pref;
      })
    );
  };

  const unsubscribeRequestedType = () => {
    const category = publicData?.requested_category || "all_optional";
    submitPublicPreferences({ category, is_subscribed: false });
  };

  const unsubscribeAllOptional = () => {
    submitPublicPreferences({
      category: publicData?.requested_category || "all_optional",
      is_subscribed: false,
      all_optional: true,
    });
  };

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8">
      <div className="mx-auto w-full max-w-[680px]">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
          <div className="bg-rose-500 px-6 py-4">
            <h1 className="text-xl font-semibold text-white md:text-2xl">
              Email Preferences
            </h1>
          </div>
          <div className="space-y-4 px-6 py-6 text-sm md:text-base">
            {isPreview && (
              <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-blue-700">
                Preview only. This shows what clients see, but it will not save changes.
              </div>
            )}
            {!token && !resolvedPrefToken && !isPreview && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                Missing or invalid token.
              </div>
            )}
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {error}
              </div>
            )}
            {success && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700">
                {success}
              </div>
            )}

            {isPreferenceCenter && (
              <>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700">
                  <div className="font-semibold text-slate-900">
                    {publicData?.salon_name || "Your salon"}
                  </div>
                  <div className="mt-1 text-sm text-slate-500">
                    {publicData?.client_name ? `${publicData.client_name} · ` : ""}
                    {publicData?.email || "Client email"}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase text-slate-500">
                    Reason
                  </label>
                  <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                  >
                    <option value="">Select a reason (optional)</option>
                    {reasonOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold uppercase text-slate-500">
                    Additional details
                  </label>
                  <textarea
                    rows="3"
                    value={detail}
                    onChange={(e) => setDetail(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    placeholder="Optional"
                  />
                </div>

                <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  {publicPreferences.map((pref) => {
                    const blockedByAll =
                      pref.category !== "all_optional" && !allOptionalSubscribed;
                    const checked = blockedByAll ? false : pref.is_subscribed;
                    return (
                      <label
                        key={pref.category}
                        className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-3 py-3"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) =>
                            updateLocalPublicPreference(pref.category, e.target.checked)
                          }
                          disabled={loading}
                          className="mt-1 h-4 w-4 rounded border-slate-300"
                        />
                        <span>
                          <span className="block font-semibold text-slate-900">
                            {pref.label}
                          </span>
                          <span className="block text-xs text-slate-500">
                            {blockedByAll
                              ? "Stopped because all optional emails are off."
                              : pref.description}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={savePublicPreferences}
                    disabled={!resolvedPrefToken || loading}
                    className="rounded-full bg-rose-500 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                  >
                    {loading ? "Saving..." : "Save preferences"}
                  </button>
                  <button
                    type="button"
                    onClick={unsubscribeRequestedType}
                    disabled={!resolvedPrefToken || loading}
                    className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 disabled:opacity-60"
                  >
                    Unsubscribe from this type
                  </button>
                  <button
                    type="button"
                    onClick={unsubscribeAllOptional}
                    disabled={!resolvedPrefToken || loading}
                    className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 disabled:opacity-60"
                  >
                    Unsubscribe from all optional emails
                  </button>
                </div>

                <p className="text-xs text-slate-500">
                  Receipts, password resets, and critical account emails may still be sent.
                </p>
              </>
            )}

            {!isPreferenceCenter && (
              <>
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-slate-500">
                    Reason
                  </label>
                  <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                  >
                    <option value="">Select a reason (optional)</option>
                    {reasonOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-500">
                    Additional details (optional)
                  </label>
                  <textarea
                    rows="3"
                    value={detail}
                    onChange={(e) => setDetail(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>

                <div className="space-y-2 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs font-semibold uppercase text-slate-500">
                    Preferred content
                  </div>
                  {Object.keys(preferences).map((key) => (
                    <label
                      key={key}
                      className="flex items-center gap-2 text-sm text-slate-700"
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(preferences[key])}
                        onChange={(e) =>
                          setPreferences((prev) => ({
                            ...prev,
                            [key]: e.target.checked,
                          }))
                        }
                        className="h-4 w-4 rounded border-slate-300"
                      />
                      {key.replace(/_/g, " ")}
                    </label>
                  ))}
                </div>

                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={hardOptout}
                    onChange={(e) => setHardOptout(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                  Do not email me about promotions
                </label>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => submitLegacyMarketing(false)}
                    disabled={!token || loading}
                    className="rounded-full bg-rose-500 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                  >
                    {loading ? "Submitting..." : "Unsubscribe"}
                  </button>
                  <button
                    type="button"
                    onClick={() => submitLegacyMarketing(true)}
                    disabled={!token || loading}
                    className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 disabled:opacity-60"
                  >
                    Update Preferences Instead
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
